import { Box } from "@mui/material";
import { useClientStatusLabel } from "@shared";
import { format } from "date-fns";

import { useCounselors } from "@/common/components/counselor-select/hooks/useCounselors";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useCourts } from "@/common/hooks/useCourts";
import { ClientsFilterType } from "@/pages/clients/clients/Clients.types";
import { useTranslation } from "@/translations";

import { DeleteAllFiltersChip } from "./components/delete-filters-chip/DeleteAllFiltersChip";
import { FilterChip } from "./components/filter-chip/FilterChip";

interface Props {
  filters: ClientsFilterType;
  onFiltersUpdate: <T extends keyof ClientsFilterType>(key: T, filters: ClientsFilterType[T]) => Promise<void>;
  onAllFiltersRemove: () => void;
}

export function ClientsFilters({ filters, onFiltersUpdate, onAllFiltersRemove }: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const { userCodeOf } = useCounselors();
  const { courts } = useCourts();
  const statusLabelOf = useClientStatusLabel();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
      {(Object.keys(filters) as Array<keyof ClientsFilterType>)
        .filter((key) => filters[key] != null)
        .map((key) => (
          <FilterChip
            key={key}
            label={t(`clients.filters.${key}`, { value: stringifyValue(key, filters[key]) })}
            onDelete={async () => await onFiltersUpdate(key, null)}
          />
        ))}
      <DeleteAllFiltersChip onClick={onAllFiltersRemove} />
    </Box>
  );

  function stringifyValue<T extends keyof ClientsFilterType>(key: T, value: ClientsFilterType[T]) {
    if (value === null) {
      return null;
    }

    switch (key) {
      case "caseNoteDateFrom":
      case "caseNoteDateTo":
      case "birthday":
        return format(value, "P", { locale: locales[language] });
      case "assignedCounselorId":
        return userCodeOf(value as string) ?? value;
      case "courtId":
        return courts.find((c) => c.id === value)?.name ?? value;
      case "status":
        return statusLabelOf(value as string);
      default:
        return value;
    }
  }
}
